import { type PropsWithChildren } from 'react';
import { BrowserRouter, Navigate, Route } from 'react-router-dom';
import { apiDocsPlugin, ApiExplorerPage } from '@backstage/plugin-api-docs';
import {
  CatalogEntityPage,
  CatalogIndexPage,
  catalogPlugin,
} from '@backstage/plugin-catalog';
import {
  CatalogImportPage,
  catalogImportPlugin,
} from '@backstage/plugin-catalog-import';
import { ScaffolderPage, scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { orgPlugin } from '@backstage/plugin-org';
import { SearchPage } from '@backstage/plugin-search';
import {
  TechDocsIndexPage,
  techdocsPlugin,
  TechDocsReaderPage,
} from '@backstage/plugin-techdocs';
import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { Mermaid } from 'backstage-plugin-techdocs-addon-mermaid';
import { ReportIssue } from '@backstage/plugin-techdocs-module-addons-contrib';
import { UserSettingsPage } from '@backstage/plugin-user-settings';
import { LitellmPage } from '@litellm/plugin-litellm';
import { apis } from './apis';
import { entityPage } from './components/catalog/EntityPage';
import { searchPage } from './components/search/SearchPage';
import { Root } from './components/Root';

import {
  AlertDisplay,
  OAuthRequestDialog,
  SignInPage,
} from '@backstage/core-components';
import { createApp } from '@backstage/app-defaults';
import {
  alertApiRef,
  createApiFactory,
  githubAuthApiRef,
} from '@backstage/core-plugin-api';
import { AppRouter, FlatRoutes } from '@backstage/core-app-api';
import { CatalogGraphPage } from '@backstage/plugin-catalog-graph';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha';
import { NotificationsPage } from '@backstage/plugin-notifications';
import { SignalsDisplay } from '@backstage/plugin-signals';
import { toastApiRef } from '@backstage/frontend-plugin-api';

import { graderaLightTheme, graderaDarkTheme } from './theme';
import { UnifiedThemeProvider } from '@backstage/theme';

// Opt-in to React Router v7 future behaviours to silence the upgrade warnings
// introduced in react-router 6.26+ (v7_startTransition, v7_relativeSplatPath).
// Safe to enable: both flags adopt the v7-default behaviour with no breaking
// changes on our existing route tree.
const Router = ({
  children,
  basename,
}: PropsWithChildren<{ basename?: string }>) => (
  <BrowserRouter
    basename={basename}
    future={{ v7_startTransition: true, v7_relativeSplatPath: true }}
  >
    {children}
  </BrowserRouter>
);

const app = createApp({
  apis: [
    ...apis,
    createApiFactory({
      api: toastApiRef,
      deps: { alertApi: alertApiRef },
      factory: ({ alertApi }) => ({
        post: toast => {
          let severity: 'error' | 'warning' | 'info' | 'success' = 'success';
          if (toast.status === 'danger') {
            severity = 'error';
          } else if (toast.status === 'warning') {
            severity = 'warning';
          } else if (toast.status === 'info') {
            severity = 'info';
          }

          alertApi.post({
            message:
              typeof toast.title === 'string' ? toast.title : 'Notification',
            severity,
            display: toast.timeout ? 'transient' : 'permanent',
          });

          return { close: () => {} };
        },
      }),
    }),
  ],
  themes: [
    {
      id: 'gradera-light',
      title: 'Gradera Light',
      variant: 'light',
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={graderaLightTheme} children={children} />
      ),
    },
    {
      id: 'gradera-dark',
      title: 'Gradera Dark',
      variant: 'dark',
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={graderaDarkTheme} children={children} />
      ),
    },
  ],
  bindRoutes({ bind }) {
    bind(catalogPlugin.externalRoutes, {
      createComponent: scaffolderPlugin.routes.root,
      viewTechDoc: techdocsPlugin.routes.docRoot,
      createFromTemplate: scaffolderPlugin.routes.selectedTemplate,
    });
    bind(apiDocsPlugin.externalRoutes, {
      registerApi: catalogImportPlugin.routes.importPage,
    });
    bind(scaffolderPlugin.externalRoutes, {
      registerComponent: catalogImportPlugin.routes.importPage,
      viewTechDoc: techdocsPlugin.routes.docRoot,
    });
    bind(orgPlugin.externalRoutes, {
      catalogIndex: catalogPlugin.routes.catalogIndex,
    });
  },
  components: {
    Router,
    SignInPage: props => (
      <SignInPage
        {...props}
        auto
        providers={[
          {
            id: 'github-auth-provider',
            title: 'GitHub',
            message: 'Sign in using GitHub',
            apiRef: githubAuthApiRef,
          },
        ]}
      />
    ),
  },
});

const routes = (
  <FlatRoutes>
    <Route path="/" element={<Navigate to="catalog" />} />
    <Route path="/catalog" element={<CatalogIndexPage />} />
    <Route
      path="/catalog/:namespace/:kind/:name"
      element={<CatalogEntityPage />}
    >
      {entityPage}
    </Route>
    <Route path="/docs" element={<TechDocsIndexPage />} />
    <Route
      path="/docs/:namespace/:kind/:name/*"
      element={<TechDocsReaderPage />}
    >
      <TechDocsAddons>
        <Mermaid />
        <ReportIssue />
      </TechDocsAddons>
    </Route>
    <Route path="/create" element={<ScaffolderPage />} />
    <Route path="/litellm" element={<LitellmPage />} />
    <Route path="/api-docs" element={<ApiExplorerPage />} />
    <Route
      path="/catalog-import"
      element={
        <RequirePermission permission={catalogEntityCreatePermission}>
          <CatalogImportPage />
        </RequirePermission>
      }
    />
    <Route path="/search" element={<SearchPage />}>
      {searchPage}
    </Route>
    <Route path="/settings" element={<UserSettingsPage />} />
    <Route path="/catalog-graph" element={<CatalogGraphPage />} />
    <Route path="/notifications" element={<NotificationsPage />} />
  </FlatRoutes>
);

export default app.createRoot(
  <>
    <AlertDisplay />
    <OAuthRequestDialog />
    <SignalsDisplay />
    <AppRouter>
      <Root>{routes}</Root>
    </AppRouter>
  </>,
);
